const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

export interface BedrockUsageStats {
  totalRequests: number;
  totalTokens: number;
  requestsByType: Record<string, number>;
  requestsByModel?: Record<string, number>;
}

export interface BedrockUsageRecord {
  id: number;
  org_id: number;
  user_id: number;
  username: string;
  model_id: string;
  actual_tokens: number;
  request_type: string;
  created_at: string;
}

export interface OrganizationUsage {
  orgId: number;
  orgName: string;
  totalTokens: number;
  totalRequests: number;
  topModel: string;
}

export interface UserUsage {
  username: string;
  userId: number;
  orgId: number;
  tokens: number;
  requests: number;
  topFeature?: string;
}

export interface SystemAnalytics {
  totalTokens: number;
  totalRequests: number;
  period: string;
  byOrganization: Record<string, { tokens: number; requests: number }>;
  byModel: Record<string, { tokens: number; requests: number }>;
  byRequestType: Record<string, { tokens: number; requests: number }>;
  byUser: Record<string, { tokens: number; requests: number }>;
}

export interface Organization {
  id: number;
  name: string;
  subdomain?: string;
}

export interface ToolServerSummaryRow {
  tool_server_id: number;
  tool_server_name: string;
  api_key_prefix: string;
  total_calls: number;
  sum_input_tokens: number;
  sum_output_tokens: number;
  sum_actual_tokens: number;
  orgs_count: number;
  users_count: number;
  last_call: string;
}

export interface OrgUsageRow {
  org_id: number;
  org_name: string;
  total_calls: number;
  sum_input_tokens: number;
  sum_output_tokens: number;
  sum_actual_tokens: number;
  via_this_server: number;
  via_other_servers: number;
  direct_calls: number;
  active_users: number;
  last_call: string;
}

export interface UserUsageRow {
  user_id: number;
  username: string;
  total_calls: number;
  sum_input_tokens: number;
  sum_output_tokens: number;
  sum_actual_tokens: number;
  via_this_server: number;
  direct_calls: number;
  top_model: string;
  top_request_type: string;
  last_call: string;
}

export interface CallLogRow {
  id: number;
  created_at: string;
  model_id: string;
  request_type: string;
  input_tokens: number | null;
  output_tokens: number | null;
  actual_tokens: number;
  tool_server_ids: number[];
}

export interface CallLogResult {
  rows: CallLogRow[];
  total: number;
  page: number;
  limit: number;
}

export const bedrockUsageService = {
  /**
   * Get all organizations (for name mapping)
   */
  getOrganizations: async (): Promise<Organization[]> => {
    const response = await fetch(`${API_URL}/super-admin/organizations`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch organizations: ${response.statusText}`);
    }

    const result = await response.json();
    return result.data || [];
  },

  /**
   * Get current user's usage statistics
   */
  getUserStats: async (days: number = 30): Promise<BedrockUsageStats> => {
    const response = await fetch(`${API_URL}/bedrock-usage/user/stats?days=${days}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch user stats: ${response.statusText}`);
    }

    const result = await response.json();
    return result.data;
  },

  /**
   * Get current user's usage records
   */
  getUserUsage: async (limit: number = 50): Promise<BedrockUsageRecord[]> => {
    const response = await fetch(`${API_URL}/bedrock-usage/user/usage?limit=${limit}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch user usage: ${response.statusText}`);
    }

    const result = await response.json();
    return result.data;
  },

  /**
   * Get organization usage statistics
   */
  getOrgStats: async (orgId?: number, days: number = 30): Promise<BedrockUsageStats> => {
    const url = orgId
      ? `${API_URL}/bedrock-usage/org/stats?orgId=${orgId}&days=${days}`
      : `${API_URL}/bedrock-usage/org/stats?days=${days}`;

    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch org stats: ${response.statusText}`);
    }

    const result = await response.json();
    return result.data;
  },

  /**
   * Get organization usage records
   */
  getOrgUsage: async (orgId?: number, limit: number = 100): Promise<BedrockUsageRecord[]> => {
    const url = orgId
      ? `${API_URL}/bedrock-usage/org/usage?orgId=${orgId}&limit=${limit}`
      : `${API_URL}/bedrock-usage/org/usage?limit=${limit}`;

    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch org usage: ${response.statusText}`);
    }

    const result = await response.json();
    return result.data;
  },

  /**
   * Get top users by token usage
   */
  getTopUsers: async (days: number = 30, limit: number = 10): Promise<UserUsage[]> => {
    const response = await fetch(
      `${API_URL}/bedrock-usage/org/top-users?days=${days}&limit=${limit}`,
      {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch top users: ${response.statusText}`);
    }

    const result = await response.json();
    return result.data;
  },

  /**
   * Get usage breakdown by request type
   */
  getUsageByType: async (days: number = 30): Promise<Array<{ requestType: string; tokens: number; requests: number }>> => {
    const response = await fetch(`${API_URL}/bedrock-usage/org/by-type?days=${days}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch usage by type: ${response.statusText}`);
    }

    const result = await response.json();
    return result.data;
  },

  /**
   * Get usage by date range
   */
  getUsageByDateRange: async (
    startDate: string,
    endDate: string,
    orgId?: number
  ): Promise<BedrockUsageRecord[]> => {
    const url = orgId
      ? `${API_URL}/bedrock-usage/date-range?startDate=${startDate}&endDate=${endDate}&orgId=${orgId}`
      : `${API_URL}/bedrock-usage/date-range?startDate=${startDate}&endDate=${endDate}`;

    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch usage by date range: ${response.statusText}`);
    }

    const result = await response.json();
    return result.data;
  },

  /**
   * Get system-wide analytics (Super admin only)
   */
  getSystemAnalytics: async (days: number = 30): Promise<SystemAnalytics> => {
    const response = await fetch(`${API_URL}/bedrock-usage/system/analytics?days=${days}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch system analytics: ${response.statusText}`);
    }

    const result = await response.json();
    return result.data;
  },

  getToolServerSummary: async (startDate?: string, endDate?: string): Promise<ToolServerSummaryRow[]> => {
    const params = new URLSearchParams();
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await fetch(`${API_URL}/bedrock-usage/tool-server/summary${query}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      },
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch tool server summary: ${response.statusText}`);
    }
    const result = await response.json();
    return result.data;
  },

  getOrgsByToolServer: async (toolServerId: number, startDate?: string, endDate?: string): Promise<OrgUsageRow[]> => {
    const params = new URLSearchParams();
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await fetch(`${API_URL}/bedrock-usage/tool-server/${toolServerId}/orgs${query}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      },
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch orgs for tool server: ${response.statusText}`);
    }
    const result = await response.json();
    return result.data;
  },

  getUsersByToolServerOrg: async (toolServerId: number, orgId: number, startDate?: string, endDate?: string): Promise<UserUsageRow[]> => {
    const params = new URLSearchParams();
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await fetch(`${API_URL}/bedrock-usage/tool-server/${toolServerId}/orgs/${orgId}/users${query}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      },
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch users for org: ${response.statusText}`);
    }
    const result = await response.json();
    return result.data;
  },

  getUserCallLog: async (userId: number, orgId: number, page: number = 1, limit: number = 50, startDate?: string, endDate?: string): Promise<CallLogResult> => {
    const params = new URLSearchParams();
    params.set('orgId', String(orgId));
    params.set('page', String(page));
    params.set('limit', String(limit));
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    const response = await fetch(`${API_URL}/bedrock-usage/user/${userId}/call-log?${params.toString()}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      },
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch user call log: ${response.statusText}`);
    }
    const result = await response.json();
    return {
      rows: result.data,
      total: result.total,
      page: result.page,
      limit: result.limit,
    };
  },
};

